import { forwardRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentFocusProperty, selectUpdatingTypeField } from '../../../store/selectors';
import { FieldType as FieldTypeEnum } from '../../../../../types/custom-worksheet-v2';
import { editField, editTableColumnSummary, setUpdatingTypeFieldId } from '../../../store/slice';
import { PencilIcon } from '@heroicons/react/20/solid';
import { canSummarise } from '../../../store/utils';
import { formatMixedCase } from '../../../../../utilities/formatting';
import FieldIcon from '../../../../../components/custom-worksheet/FieldIcon';
import EditInput from '../EditInput';

type FieldTypeProps = {
    id: string;
    fieldType: FieldTypeEnum;
    showActionButtons?: boolean;
};

const FieldType = forwardRef(function FieldType(
    { id, fieldType, showActionButtons = true }: FieldTypeProps,
    ref: React.ForwardedRef<HTMLDivElement>
) {
    const dispatch = useDispatch();
    const currentFocusProperty = useSelector(selectCurrentFocusProperty);
    const currentUpdatingTypeField = useSelector(selectUpdatingTypeField);

    const isUpdatingType = currentUpdatingTypeField === id;
    const isFieldSidePanelOpen = currentFocusProperty === id;

    const [type, setType] = useState<FieldTypeEnum | undefined>(fieldType);

    useEffect(() => {
        setType(fieldType);
    }, [id, fieldType]);

    function handleResetValue(): void {
        setType(fieldType);
        dispatch(setUpdatingTypeFieldId(undefined));
    }

    function handleSaveType(): void {
        // If field value is an empty string, revert to the previous value
        if (!type) {
            setType(fieldType);
            dispatch(setUpdatingTypeFieldId(undefined));
            return;
        }

        // update type
        if (type !== fieldType) {
            /**
             * When type changes
             * - reset default value
             * - reset reconciliation if type changes from MONEY to another type
             */
            dispatch(
                editField({
                    id,
                    details: { type, defaultValue: null, reconciliation: fieldType === FieldTypeEnum.MONEY ? null : undefined },
                })
            );
            // Update if property can still be summarised
            dispatch(editTableColumnSummary({ id, value: { enabled: canSummarise(type), reconciliation: null } }));
        }
        dispatch(setUpdatingTypeFieldId(undefined));
    }

    if (!showActionButtons) {
        return (
            <EditInput
                inputType="type"
                placeholder="Select a field type..."
                value={type}
                showActionButtons={false}
                autoFocus={false}
                onChange={(val): void => setType(val ? (val as FieldTypeEnum) : undefined)}
                onSave={handleSaveType}
                onCancel={handleResetValue}
            />
        );
    }

    return (
        <>
            {isUpdatingType ? (
                <EditInput
                    inputType="type"
                    placeholder="Select a field type..."
                    value={type}
                    onChange={(val): void => setType(val ? (val as FieldTypeEnum) : undefined)}
                    onSave={handleSaveType}
                    onCancel={handleResetValue}
                />
            ) : (
                <div
                    ref={ref}
                    className={`w-max ${
                        isFieldSidePanelOpen ? 'cursor-default' : 'cursor-pointer'
                    } hover:bg-gray-200 px-2 py-0.5 rounded-[5px] flex gap-3 items-center group border border-transparent`}
                    onClick={(): void => {
                        if (isFieldSidePanelOpen) {
                            return;
                        }
                        setType(fieldType);
                        dispatch(setUpdatingTypeFieldId(id));
                    }}
                >
                    <div className="flex gap-1 items-center">
                        <div className="w-3.5 h-3.5">
                            <FieldIcon type={fieldType} />
                        </div>
                        <p className="p-1 text-sm text-gray-700">{formatMixedCase(fieldType.toString())}</p>
                    </div>
                    <PencilIcon className="text-gray-500 w-4 h-4 hidden group-hover:block" />
                </div>
            )}
        </>
    );
});

export default FieldType;
